<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CompaniesForm from 'dashboard/components-next/Companies/CompaniesForm/CompaniesForm.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';

defineProps({
  isLoading: { type: Boolean, default: false },
});

const emit = defineEmits(['create']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const companiesFormRef = ref(null);
const createContactDialogRef = ref(null);
const formData = ref(null);

const uiFlags = useMapGetter('contacts/getUIFlags');
const isCreatingContact = computed(() => uiFlags.value.isCreating);

const handleFormUpdate = data => {
  formData.value = data;
};

const handleDialogConfirm = async () => {
  const isFormValid = await companiesFormRef.value?.validate();
  if (!isFormValid || !formData.value) return;
  const data = companiesFormRef.value?.state || formData.value;
  emit('create', {
    name: data.name?.trim(),
    domain: data.domain?.trim() || null,
    description: data.description?.trim() || null,
    email: data.email?.trim() || null,
    phoneNumber: data.phoneNumber?.trim() || null,
    city: data.city?.trim() || null,
    countryCode: data.countryCode || null,
    socialProfiles: data.socialProfiles || {},
    cnpj: data.cnpj?.trim() || null,
    contactId: data.contactId || null,
    customAttributes: data.customAttributes || {},
    labels: companiesFormRef.value?.selectedLabels || [],
  });
};

const resetState = () => {
  companiesFormRef.value?.resetForm();
  formData.value = null;
};

const onSuccess = () => {
  resetState();
  dialogRef.value?.close();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const openCreateContact = () => {
  createContactDialogRef.value?.dialogRef.open();
};

const handleContactCreated = async contact => {
  try {
    const newContact = await store.dispatch('contacts/create', contact);
    companiesFormRef.value?.setContactId(newContact.id);
    createContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
  }
};

defineExpose({ dialogRef, onSuccess });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    @confirm="handleDialogConfirm"
    @close="resetState"
  >
    <CompaniesForm
      ref="companiesFormRef"
      is-new-company
      @update="handleFormUpdate"
    >
      <template #contact-link-action>
        <button
          type="button"
          class="self-start text-xs text-n-brand hover:underline"
          @click="openCreateContact"
        >
          {{
            t(
              'COMPANIES_LAYOUT.CARD.EDIT_DETAILS_FORM.CONTACT_LINK.CREATE_ACTION'
            )
          }}
        </button>
      </template>
    </CompaniesForm>
    <CreateNewContactDialog
      ref="createContactDialogRef"
      :is-loading="isCreatingContact"
      @create="handleContactCreated"
    />
    <template #footer>
      <div class="flex items-center w-full gap-3">
        <Button
          type="submit"
          :label="t('COMPANIES.CREATE.ACTIONS.SAVE')"
          color="blue"
          :is-loading="isLoading"
        />
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          type="reset"
          @click="closeDialog"
        />
      </div>
    </template>
  </Dialog>
</template>
